import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
  WarningCircleIcon,
} from '@noor/ui';
import type { ModalRenderProps } from '@noor/lib';

/**
 * Built-in confirmation modal, pixel-matched to the Noor-CPanel Figma design
 * (node 749:19612): a 480px card with a centered 72px orange warning circle
 * over the title + description, and two full-width standard dialog actions
 * (soft cancel, dark/destructive confirm).
 * Receives its payload as a typed prop from the host and resolves the
 * `confirmDialog()` promise via `onResolve`. Registered in the app modal
 * registry under the `confirm` id.
 *
 * Every settle path — the confirm button, the cancel button, and Esc
 * dismissal (via `onOpenChange`) — goes through `resolve`, which settles at
 * most once. Outside clicks never dismiss (the shared `DialogContent` blocks
 * them) so an accidental backdrop click can't settle the promise. This
 * deliberately avoids an unmount-effect guard, which would resolve `false`
 * during React StrictMode's dev mount/unmount probe and pre-empt the user's
 * real choice.
 */
export function ConfirmModal({ payload, close }: ModalRenderProps<'confirm'>) {
  const {
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onResolve,
  } = payload;
  const [pending, setPending] = useState(false);

  // Settle the promise exactly once, from whichever path fires first — a button
  // press or Esc dismissal. Outside clicks are blocked by `DialogContent`, so
  // they never settle. A ref (not an unmount effect) keeps this
  // StrictMode-safe: an unmount-cleanup guard would resolve `false` during
  // StrictMode's dev mount/unmount probe, pre-empting the user's real choice.
  const settledRef = useRef(false);
  const resolve = (confirmed: boolean): void => {
    if (!settledRef.current) {
      settledRef.current = true;
      onResolve(confirmed);
    }
    close();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!onConfirm) {
      resolve(true);
      return;
    }

    setPending(true);
    try {
      await onConfirm();
      resolve(true);
    } catch {
      setPending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => (next || pending ? undefined : resolve(false))}>
      <DialogContent
        showClose={false}
        className="max-w-[calc(100%-2rem)] gap-8 rounded-3xl p-6 sm:max-w-[30rem]"
      >
        <div className="flex w-full flex-col items-center gap-6 px-[42px]">
          <WarningCircleIcon className="size-[72px] shrink-0" />
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <DialogTitle className="text-[22px] leading-none font-semibold tracking-[-0.88px] text-black dark:text-foreground">
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="text-sm leading-none font-medium tracking-[-0.28px] text-[#9ca3af] dark:text-muted-foreground">
                {description}
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{title}</DialogDescription>
            )}
          </div>
        </div>

        <div className="flex w-full gap-2">
          <Button
            type="button"
            variant="soft"
            shape="rounded"
            size="modal"
            className="flex-1"
            onClick={() => resolve(false)}
            disabled={pending}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'dark'}
            shape="rounded"
            size="modal"
            className="flex-1"
            onClick={() => void handleConfirm()}
            disabled={pending}
            aria-busy={pending}
          >
            {pending && <Spinner size="sm" className="text-current" />}
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
